import React, { useState, useEffect, useRef } from 'react';
import { videoAPI, imagaeAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button } from 'react-bootstrap';

const Videos = () => {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        video_embed: '',
        duration: '',
        order: 0,
        status: true,
        thumbnail: null
    });

    const thumbnailRef = useRef(null);
    const editThumbnailRef = useRef(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await videoAPI.getAll();
            setItems(res.data.data);
        } catch (error) {
            console.error("Error fetching videos:", error);
            toast.error("Failed to load videos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        if (type === 'file') {
            setFormData(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Extract YouTube video ID from URL
    const getYouTubeId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    // Extract Vimeo video ID
    const getVimeoId = (url) => {
        const regExp = /(?:vimeo)\.com.*?(?:video\/)?(\d+)/;
        const match = url.match(regExp);
        return match ? match[1] : null;
    };

    // Get embed HTML from URL
    const getEmbedHtml = (url) => {
        if (!url) return '';

        if (url.includes('<iframe') || url.includes('embed')) {
            return url;
        }

        const youtubeId = getYouTubeId(url);
        if (youtubeId) {
            return `<iframe width="100%" height="315" src="https://www.youtube.com/embed/${youtubeId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
        }

        const vimeoId = getVimeoId(url);
        if (vimeoId) {
            return `<iframe src="https://player.vimeo.com/video/${vimeoId}" width="100%" height="315" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
        }

        return url;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            data.append("title", formData.title);
            data.append("video_embed", formData.video_embed);
            data.append("duration", formData.duration);
            data.append("order", formData.order);
            data.append("status", formData.status ? 1 : 0);

            if (formData.thumbnail) {
                data.append("thumbnail", formData.thumbnail);
            }

            await videoAPI.create(data);
            toast.success('Video added successfully!');
            setFormData({
                title: '',
                video_embed: '',
                duration: '',
                order: 0,
                status: true,
                thumbnail: null
            });
            if (thumbnailRef.current) thumbnailRef.current.value = "";
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error adding video: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (item) => {
        setSelectedItem({
            id: item.id,
            title: item.title,
            video_embed: item.video_embed,
            duration: item.duration || '',
            order: item.order,
            status: item.status,
            thumbnail: item.thumbnail,
            _original_thumbnail: item.thumbnail
        });
        setShowEditModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value, files, type, checked } = e.target;
        if (type === 'file') {
            setSelectedItem(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else if (type === 'checkbox') {
            setSelectedItem(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setSelectedItem(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleUpdateItem = async () => {
        setLoading(true);
        try {
            const data = new FormData();
            data.append("title", selectedItem.title);
            data.append("video_embed", selectedItem.video_embed);
            data.append("duration", selectedItem.duration);
            data.append("order", selectedItem.order);
            data.append("status", selectedItem.status ? 1 : 0);

            if (selectedItem.thumbnail && selectedItem.thumbnail instanceof File) {
                data.append("thumbnail", selectedItem.thumbnail);
            }

            await videoAPI.update(selectedItem.id, data);
            toast.success('Video updated successfully!');
            setShowEditModal(false);
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error updating video: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        setLoading(true);
        try {
            await videoAPI.toggleStatus(id);
            toast.success(`Video ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error('Error updating status: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleWatchVideo = (item) => {
        setCurrentVideo(item);
        setShowVideoModal(true);
    };

    const confirmDelete = (id) => {
        toast.info(
            <div>
                <p style={{ marginBottom: '0.5rem' }}>Are you sure you want to delete this video?</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        className="btn btn-sm btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => { toast.dismiss(); handleDeleteItem(id); }}
                    >
                        Yes
                    </button>
                    <button
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        onClick={() => toast.dismiss()}
                    >
                        No
                    </button>
                </div>
            </div>,
            { autoClose: false, closeOnClick: false, closeButton: false, icon: false }
        );
    };

    const handleDeleteItem = async (id) => {
        setLoading(true);
        try {
            await videoAPI.delete(id);
            toast.success("Video deleted successfully!");
            fetchItems();
        } catch (error) {
            console.error(error);
            toast.error("Error deleting video: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    // ----- inline styles (same as Photos) -----
    const mainContentStyle = {
        marginLeft: '260px',
        padding: '20px 30px',
        backgroundColor: '#f4f7fc',
        minHeight: '100vh',
    };

    const contentWrapperStyle = {
        maxWidth: '1200px',
        margin: '0 auto',
    };

    const formCardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        marginBottom: '25px',
    };

    const formCardHeaderStyle = {
        background: 'linear-gradient(135deg, #0b2b3b 0%, #0a1c28 100%)',
        padding: '16px 24px',
        color: 'white',
    };

    const formCardHeaderTitleStyle = {
        margin: 0,
        fontSize: '1rem',
        fontWeight: 600,
    };

    const formCardBodyStyle = {
        padding: '24px',
    };

    const formRowStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '1rem',
    };

    const formGroupStyle = (flex = 1) => ({
        flex: flex,
        minWidth: '200px',
    });

    const labelStyle = {
        display: 'block',
        marginBottom: '0.25rem',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1e293b',
    };

    const inputStyle = {
        width: '100%',
        padding: '0.5rem 0.75rem',
        fontSize: '0.85rem',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        transition: 'all 0.2s',
        outline: 'none',
        background: 'white',
    };

    const textareaStyle = {
        ...inputStyle,
        resize: 'vertical',
        minHeight: '80px',
    };

    const smallTextStyle = {
        fontSize: '0.7rem',
        color: '#64748b',
        display: 'block',
        marginTop: '0.25rem',
    };

    const buttonPrimaryStyle = {
        background: 'linear-gradient(135deg, #0b2b3b 0%, #0a1c28 100%)',
        border: 'none',
        padding: '10px 25px',
        fontSize: '0.85rem',
        fontWeight: 600,
        borderRadius: '10px',
        color: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
    };

    const listCardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        marginTop: '1.5rem',
    };

    const tableContainerStyle = {
        overflowX: 'auto',
        padding: '0 1rem',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.85rem',
    };

    const tableHeaderStyle = {
        padding: '14px 20px',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        textAlign: 'left',
    };

    const tableCellStyle = {
        padding: '14px 20px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
    };

    const actionButtonStyle = {
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        borderRadius: '4px',
        border: 'none',
        cursor: 'pointer',
        marginRight: '0.25rem',
    };

    return (
        <>
            <div style={mainContentStyle}>
                <div style={contentWrapperStyle}>
                    {/* Add Form */}
                    <div style={formCardStyle}>
                        <div style={formCardHeaderStyle}>
                            <h5 style={formCardHeaderTitleStyle}>
                                <i className="fas fa-plus-circle" style={{ marginRight: '0.5rem' }}></i>Add Video
                            </h5>
                        </div>
                        <div style={formCardBodyStyle}>
                            <form onSubmit={handleSubmit}>
                                <div style={formRowStyle}>
                                    <div style={formGroupStyle(2)}>
                                        <label style={labelStyle}>Title *</label>
                                        <input
                                            type="text"
                                            style={inputStyle}
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                        <label style={labelStyle}>Duration (Optional)</label>
                                        <input
                                            type="text"
                                            style={inputStyle}
                                            name="duration"
                                            value={formData.duration}
                                            onChange={handleChange}
                                            placeholder="e.g., 5:30, 10:00"
                                        />
                                    </div>
                                    <div style={{ ...formGroupStyle(0.5), display: 'none' }}>
                                        <label style={labelStyle}>Display Order</label>
                                        <input
                                            type="number"
                                            style={inputStyle}
                                            name="order"
                                            value={formData.order}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div style={formRowStyle}>
                                    <div style={formGroupStyle(2)}>
                                        <label style={labelStyle}>Video Embed Code or URL *</label>
                                        <textarea
                                            style={textareaStyle}
                                            name="video_embed"
                                            value={formData.video_embed}
                                            onChange={handleChange}
                                            rows="3"
                                            placeholder="Paste YouTube URL (e.g., https://youtu.be/...), Embed code, or Vimeo URL"
                                            required
                                        />
                                        <small style={smallTextStyle}>
                                            Supports: YouTube URL, Vimeo URL, or complete iframe embed code
                                        </small>
                                    </div>
                                    <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                        <label style={labelStyle}>Thumbnail Image (Optional)</label>
                                        <input
                                            type="file"
                                            style={inputStyle}
                                            name="thumbnail"
                                            onChange={handleChange}
                                            accept="image/*"
                                            ref={thumbnailRef}
                                        />
                                        <small style={smallTextStyle}>Upload a custom thumbnail</small>
                                    </div>
                                </div>
                                <div style={{ ...formRowStyle, display: 'none' }}>
                                    <div style={formGroupStyle(1)}>
                                        <label style={labelStyle}>Status</label>
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <input
                                                type="checkbox"
                                                name="status"
                                                checked={formData.status}
                                                onChange={handleChange}
                                            /> Active
                                        </div>
                                    </div>
                                </div>

                                {/* Live Preview */}
                                {formData.video_embed && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <label style={labelStyle}>Preview</label>
                                        <div style={{ maxWidth: '560px' }}>
                                            <div dangerouslySetInnerHTML={{ __html: getEmbedHtml(formData.video_embed) }} />
                                        </div>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    style={buttonPrimaryStyle}
                                    disabled={loading}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'none'}
                                >
                                    {loading ? "Adding..." : "Add Video"}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* List Table */}
                    <div style={listCardStyle}>
                        <div style={tableContainerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={tableHeaderStyle}>ID</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Thumbnail</th>
                                        <th style={tableHeaderStyle}>Title</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Duration</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Order</th>
                                        <th style={{ ...tableHeaderStyle, display: 'none' }}>Status</th>
                                        <th style={tableHeaderStyle}>Watch</th>
                                        <th style={tableHeaderStyle}>Edit</th>
                                        <th style={tableHeaderStyle}>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id} style={{ transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={tableCellStyle}>{item.id}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>
                                                {item.thumbnail ? (
                                                    <img
                                                        src={`${imagaeAPI}/uploads/videos/${item.thumbnail}`}
                                                        alt={item.title}
                                                        width="80"
                                                        height="50"
                                                        style={{ objectFit: "cover", cursor: "pointer", borderRadius: '4px' }}
                                                        onClick={() => window.open(`${imagaeAPI}/uploads/videos/${item.thumbnail}`, "_blank")}
                                                    />
                                                ) : (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <i className="fas fa-video" style={{ fontSize: '30px', color: '#ccc' }}></i>
                                                    </div>
                                                )}
                                            </td>
                                            <td style={tableCellStyle}>{item.title}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>{item.duration || '-'}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>{item.order}</td>
                                            <td style={{ ...tableCellStyle, display: 'none' }}>
                                                <button
                                                    style={{
                                                        ...actionButtonStyle,
                                                        background: item.status ? '#28a745' : '#6c757d',
                                                        color: 'white'
                                                    }}
                                                    onClick={() => handleToggleStatus(item.id, item.status)}
                                                    disabled={loading}
                                                >
                                                    {item.status ? 'Active' : 'Inactive'}
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <button
                                                    style={{ ...actionButtonStyle, background: '#17a2b8', color: 'white' }}
                                                    onClick={() => handleWatchVideo(item)}
                                                >
                                                    <i className="fas fa-play"></i> Watch
                                                </button>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <a href="#" onClick={(e) => { e.preventDefault(); handleEditClick(item); }} style={{ color: '#2c7da0', textDecoration: 'none' }}>
                                                    <i className="fas fa-edit"></i>
                                                </a>
                                            </td>
                                            <td style={tableCellStyle}>
                                                <a href="#" onClick={(e) => { e.preventDefault(); confirmDelete(item.id); }} style={{ color: '#dc2626', textDecoration: 'none' }}>
                                                    <i className="fas fa-trash"></i>
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <ToastContainer />
                    </div>
                </div>
            </div>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Video</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem && (
                        <form>
                            <div style={formGroupStyle(1)}>
                                <label style={labelStyle}>Title *</label>
                                <input
                                    type="text"
                                    style={inputStyle}
                                    name="title"
                                    value={selectedItem.title}
                                    onChange={handleEditChange}
                                    required
                                />
                            </div>
                            <div style={{ ...formRowStyle, display: 'none' }}>
                                <div style={formGroupStyle(2)}>
                                    <label style={labelStyle}>Duration</label>
                                    <input
                                        type="text"
                                        style={inputStyle}
                                        name="duration"
                                        value={selectedItem.duration}
                                        onChange={handleEditChange}
                                        placeholder="e.g., 5:30"
                                    />
                                </div>
                                <div style={formGroupStyle(0.5)}>
                                    <label style={labelStyle}>Display Order</label>
                                    <input
                                        type="number"
                                        style={inputStyle}
                                        name="order"
                                        value={selectedItem.order}
                                        onChange={handleEditChange}
                                    />
                                </div>
                            </div>
                            <div style={formGroupStyle(1)}>
                                <label style={labelStyle}>Video Embed Code or URL *</label>
                                <textarea
                                    style={textareaStyle}
                                    name="video_embed"
                                    value={selectedItem.video_embed}
                                    onChange={handleEditChange}
                                    rows="3"
                                    required
                                />
                            </div>
                            <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                <label style={labelStyle}>Thumbnail Image</label>
                                <input
                                    type="file"
                                    style={inputStyle}
                                    name="thumbnail"
                                    onChange={handleEditChange}
                                    accept="image/*"
                                    ref={editThumbnailRef}
                                />
                                {selectedItem._original_thumbnail && (
                                    <div style={{ marginTop: '0.5rem' }}>
                                        <img
                                            src={`${imagaeAPI}/uploads/videos/${selectedItem._original_thumbnail}`}
                                            alt="Current"
                                            width="120"
                                        />
                                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>Current thumbnail</p>
                                    </div>
                                )}
                            </div>
                            <div style={{ ...formGroupStyle(1), display: 'none' }}>
                                <label style={labelStyle}>Status</label>
                                <div style={{ marginTop: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        name="status"
                                        checked={selectedItem.status}
                                        onChange={handleEditChange}
                                    /> Active
                                </div>
                            </div>

                            {/* Live Preview */}
                            {selectedItem.video_embed && (
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={labelStyle}>Preview</label>
                                    <div style={{ maxWidth: '560px' }}>
                                        <div dangerouslySetInnerHTML={{ __html: getEmbedHtml(selectedItem.video_embed) }} />
                                    </div>
                                </div>
                            )}
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleUpdateItem} disabled={loading}>
                        {loading ? "Updating..." : "Update"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Watch Video Modal */}
            <Modal show={showVideoModal} onHide={() => setShowVideoModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{currentVideo?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentVideo && (
                        <div>
                            <div dangerouslySetInnerHTML={{ __html: getEmbedHtml(currentVideo.video_embed) }} />
                            {currentVideo.duration && (
                                <div style={{ marginTop: '1rem' }}>
                                    <strong>Duration:</strong> {currentVideo.duration}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowVideoModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Videos;